// Data for the Marketing & Advertising Agencies landing page
// Target: agencies, marketing managers, business owners, entrepreneurs
// Co-branded with OptimaGrowth (SEO growth partner)

export const AGENCY_CLIENTS = [
  { name: "Manchester City", note: "Brand activation" },
  { name: "McDonald's", note: "Lifestyle commercial" },
  { name: "Woolworths", note: "Lifestyle & food" },
  { name: "Takealot", note: "E-commerce scale" },
  { name: "BMW", note: "Automotive lifestyle" },
  { name: "Stellenbosch University", note: "Recruitment campaign" },
  { name: "Roche", note: "Healthcare comms" },
  { name: "IHG", note: "Hospitality films" },
];

// What we deliver under the partnership - the production line for agencies
export const AGENCY_CAPABILITIES = [
  { title: "Hero campaign film", body: "Cinematic anchor asset built around the strategic insight - the piece every other channel cuts down from.", icon: "Film" },
  { title: "Social-first cutdowns", body: "15s/9:16, 30s/1:1, 60s/16:9 - hook-led, captioned, retention-tuned for paid and organic.", icon: "Smartphone" },
  { title: "Performance ad creative", body: "Iteration sets for Meta, Google, TikTok, YouTube - tested and refreshed monthly.", icon: "TrendingUp" },
  { title: "AI-powered scale", body: "One shoot, thirty variants. AI cutdowns, language swaps, format variants - delivered at agency pace.", icon: "Sparkles" },
  { title: "Stills + motion in one production", body: "Twin asset library from a single shoot day - cuts your supplier cost in half.", icon: "Camera" },
  { title: "SEO + content (via OptimaGrowth)", body: "Closing the loop - your video gets found. Keyword strategy, on-page SEO, content velocity for the client's owned channels.", icon: "Search" },
];

// Lead magnet - the irresistible free offer
export const AGENCY_LEAD_MAGNET = {
  badge: "Free for agencies",
  title: "Free AI Concept Reel + Creative Treatment",
  subtitle: "Send us your client brief. In 72 hours we'll deliver a 60-second AI-powered concept reel and a 1-page creative treatment - yours to keep, pitch, or build on. No obligation.",
  bullets: [
    "60-second AI concept reel (vertical + horizontal cuts)",
    "1-page creative treatment with art direction, music direction, shot list",
    "Optional 30-min creative call to walk through it",
    "You can pitch it to your client as your own - we stay invisible",
    "Delivered in 72 hours, free, no obligation",
  ],
  formTitle: "Get my concept reel",
  formSubtitle: "Drop your client brief (PDF or short text) - we'll get on it.",
};

// The agency engagement models - intentionally no public pricing.
// We want every interested agency to start a conversation so we can build the relationship,
// shape the quote around their brief, and avoid anchoring/undercutting.
export const AGENCY_TIERS = [
  {
    badge: "AD HOC",
    name: "Project-by-project",
    headline: "For campaigns, launches, one-off films.",
    pricingNote: "Custom-quoted per brief",
    pricingSubnote: "Budgets shaped around your client, scope and timeline.",
    bullets: [
      "Single campaign or production scope",
      "Full pre-pro through delivery",
      "Senior producer + director + crew",
      "Multi-format deliverables built in",
      "30-day rights buyout standard",
      "Transparent line-item quote",
    ],
    cta: "Send a brief",
    href: "/contact",
  },
  {
    badge: "RETAINER",
    name: "Agency Growth Partner",
    headline: "Always-on creative capacity. Best long-term ROI.",
    pricingNote: "Monthly retainer · scoped together",
    pricingSubnote: "Volume discount vs ad hoc. Locked priority slots.",
    bullets: [
      "Dedicated producer + edit team",
      "Monthly content production block",
      "AI-powered cutdown engine included",
      "OptimaGrowth SEO + content add-on available",
      "Quarterly creative strategy review",
      "Priority booking & rapid turnaround",
    ],
    cta: "Book a strategy call",
    href: null, // uses Calendly
    featured: true,
  },
];

export const AGENCY_FAQS = [
  { q: "How does the white-label model work?", a: "We stay completely invisible to your client. Deliverables ship in your branding, files are named to your spec, and we don't show your client work in our portfolio unless you specifically authorise it. You stay the agency-of-record." },
  { q: "What does the free AI Concept Reel actually deliver?", a: "You send us your client's brief (PDF, deck, or a paragraph). Within 72 hours we deliver: (1) a 60-second AI-powered concept reel showing the visual direction, in both 16:9 and 9:16 formats; (2) a 1-page creative treatment covering visual approach, art direction, music direction and an outline shot list; (3) an optional 30-min call to talk through it. Yours to use however you wish." },
  { q: "Is the AI concept reel the same as the final production?", a: "No - it's a concept proof, not the finished commercial. The AI reel demonstrates the visual world, mood and pacing. The final production is human-led with real talent, real locations, real cinematography. AI is a creative accelerator, not a replacement." },
  { q: "How does the OptimaGrowth SEO partnership work?", a: "For agencies whose clients also need SEO and content velocity, we partner with OptimaGrowth (a specialist SEO and growth agency). You get a single point of contact for video + SEO. We can quote them together or separately - whichever suits your client billing." },
  { q: "Can you produce at agency pace?", a: "Yes. Standard turnaround on a campaign film is 3-4 weeks. Rush turnaround (10 days) is available on retainer. Social cutdowns from existing footage can ship in 48 hours." },
  { q: "What's included on the Agency Growth Partner retainer?", a: "A monthly content production block (typically one shoot day per month + post), priority booking, a dedicated producer + edit team who learn your client's brand voice, AI-powered cutdown engine for unlimited social variants, and quarterly creative strategy reviews. Volume discount kicks in vs ad hoc rates." },
  { q: "Do you work outside Cape Town?", a: "Yes. We travel internationally regularly - Africa, the Indian Ocean, Europe. We've shot for clients in Mauritius, Cameroon, Mozambique, Kenya, Tanzania and beyond. Travel and accommodation quoted transparently upfront." },
  { q: "What happens to my client's data and brief?", a: "Briefs are treated under NDA by default. We do not share, resell, or display them. Talent releases, music licensing and usage rights are all wrapped into the quote and signed off before production." },
];

export const AGENCY_HERO_IMAGE = "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=2400&q=80&auto=format&fit=crop";

// Selected agency-grade work shown on the landing page (mix of commercials, brand films, music + social)
export const AGENCY_SHOWCASE = [
  { youtubeId: "M0noCv1RZQ8", title: "Essential Flavours - Culinary Series", category: "Brand Series", tag: "Hospitality" },
  { youtubeId: "thZEEMmltaw", title: "Man Gordan - The Words That You Live By", category: "Music Video", tag: "Brand Build" },
  { youtubeId: "85al7Eaq5cM", title: "Vision Will Live - Documentary", category: "Documentary", tag: "NGO" },
  { youtubeId: "NHcp0EBHVEc", title: "Rise of Kingdoms - 7 Year Anniversary", category: "Brand Documentary", tag: "Global Campaign" },
  { youtubeId: "iNAhiKMGF18", title: "Hospitality Brand Story", category: "Brand Film", tag: "Hospitality" },
  { youtubeId: "KEwcH03tdpA", title: "Luxury Property Film", category: "Cinematic", tag: "Hospitality" },
  { youtubeId: "SzoTSqvfxAA", title: "Man Gordan - Interview Piece", category: "Brand Content", tag: "Editorial" },
  { youtubeId: "6-rXuBnDCa4", title: "Destination Lifestyle Reel", category: "Lifestyle", tag: "Travel" },
  { youtubeId: "bzukc6okzho", title: "Signature Dining", category: "Food & Beverage", tag: "Culinary" },
];
